import styleCSS from "./chart-legend.style.scss?inline"

import type { ISeriesApi, Logical, MouseEventParams, Time } from "lightweight-charts"
import { Chart } from "../../../models/historical-charts/historical-chart.models"
import { clamp } from "../../../core/helpers"
import { Series } from "../../../models/historical-charts/series"
import { ChartType } from "../../../models/historical-charts/historical-chart.enum"

type RowData = { label: string, value: number, options: any }

// this component is the tooltip shown when user mouve over a chart component
export class ChartLegendComponent extends HTMLElement {
  readonly ADD_DELAY = 200

  private _dateElem!: HTMLElement
  private _listElem!: HTMLElement

  constructor() {
    super()
  }

  connectedCallback(): void {
    const shadowRoot = this.attachShadow({ mode: 'open' })
    shadowRoot.innerHTML = `
    <style>${styleCSS}</style>
    <div class='legend-date'></div>
    <div class='divider-grey'></div>
    <ol></ol>
    `
    this._dateElem = shadowRoot.querySelector('.legend-date')!
    this._listElem = shadowRoot.querySelector('ol')!
  }

  public update(param: MouseEventParams<Time>, chart: Chart, areaSeries: Map<string, ISeriesApi<any>>, priceFormatter: Function) {
    const container = this.parentElement!

    if (!param || !param.point || !this.pointIntersects(param?.point, container) || !param.time) {
      this.style.display = 'none'
      return
    }
    this.style.display = 'flex'

    // TODO refactor this workaround to data driven, workaround flows value filtering
    let series = chart.series
    if ([ChartType.Spot_ETF_BTC_Flows_USD, ChartType.Spot_ETF_BTC_Flows_USD].includes(chart.type)) {
      series = series.filter((s: Series) => {
        const point = s.points.find(p => p.time === param.time)
        if (!point) { return false }
        const formatted = priceFormatter(point.value).slice(1, -1)
        return formatted != 0
      })
    }

    if (series.length === 0) {
      this.style.display = 'none'
      return
    }

    const data = chart.isCustomSeries ?
      this.getCustomSeriesData(series, areaSeries, param.time, param.logical) :
      this.getBuiltinSeriesData(series, areaSeries, param.time, priceFormatter)

    // console.log(data);

    const date = new Date(param.time + "T00:00:00"); // Ensures correct date in local time
    this._dateElem.innerText = date.toLocaleDateString()
    this._listElem.innerHTML = data.sort((a, b) => b.value - a.value)
      .map(d => this.generateSeriesDisplay(d, priceFormatter)).join('')

    this.positionTooltip(this, container, param.point)
  }

  private seriesDataMap = ({ series, point, viewSeries }: { series: Series, point: any, viewSeries: any }) => ({
    label: series.name,
    value: point!.value,
    options: viewSeries!.options()
  })

  private getBuiltinSeriesData(series: Series[], areaSeries: Map<string, ISeriesApi<any>>, time: Time, _priceFormatter: Function): RowData[] {
    const data = series
      .map((s) => ({
        series: s,
        point: s.points.find((p) => p.time === time),
        viewSeries: areaSeries.get(s.name)
      }))
      .filter(({ point, viewSeries }) => point && +point.value !== 0 && viewSeries?.options().visible)
      .map(this.seriesDataMap)
    return data
  }

  private getCustomSeriesData(series: Series[], areaSeries: Map<string, ISeriesApi<any>>, _time: Time, logical?: Logical): RowData[] {
    // console.log(series);
    const stackedSeries = areaSeries.get('stacked')
    if (!stackedSeries) { return [] }

    const seriesData = stackedSeries.dataByIndex(logical ?? 0);
    return series.map((s, index) => {
      // console.log(s.name, (seriesData as any).values[index]);
      return {
        label: s.name,
        value: (seriesData as any).values[index],
        options: { ...s.options }
      }
    })
  }

  positionTooltip = (tooltip: HTMLElement, container: HTMLElement, point: { x: number; y: number }) => {
    const { offsetWidth: tooltipWidth, offsetHeight: tooltipHeight } = tooltip

    const containerRect = container.getBoundingClientRect()
    const { clientWidth } = container
    const vw = window.innerWidth
    const vh = window.innerHeight

    const isRightHalf = point.x > clientWidth / 2
    const desiredX = isRightHalf
      ? containerRect.left + point.x - tooltipWidth * 0.75
      : containerRect.left + point.x + tooltipWidth * 0.25

    const left = clamp(desiredX, 0, vw - tooltipWidth)
    const top = clamp(containerRect.top + point.y + 12, 0, vh - tooltipHeight)

    tooltip.style.transform = `translate(${left}px, ${top}px)`
  }

  private pointIntersects(point: { x: number, y: number }, element: Element): boolean {
    return !(point.x < 0 || point.x > element.clientWidth || point.y < 0 || point.y > element.clientHeight)
  }

  private generateSeriesDisplay(d: RowData, priceFormatter: Function): string {
    // console.log(d.options.lineColor)
    const color = d.options.color
    return `<li> <div class='legend-left'>
                <div class='marker' style='background-color:${color}'></div>
                <p>${d.label}</p>
              </div> 
              <p class='legend-data'>${priceFormatter(d.value)}</p>
            </li>`
  }
}